/**
 * useEnsureDestinationSession (web) — single source of truth for the
 * destination → agent → session inits behind the agent switcher.
 *
 *   getLocation({search})  → resolve coords + the agent for the destination
 *   listSessions()         → look for an existing per-agent session
 *   find OR createSession  → bind agentId + destinationLocation
 *   selectSession()        → sets selectedSessionId (drives the active guide)
 *
 * Mirrors RN's hook so the web switcher's inits can't drift from the app.
 */
import { useCallback, useRef, useState } from 'react';

import { useChatSessions } from './useChatSessions';
import { useLocation } from '../context/LocationContext';
import { useToast } from '../context/ToastContext';
import { useLanguage } from '../hooks/useLanguage';

export interface EnsureResult {
  sessionId: string | null;
  agentId?: string;
}

export function useEnsureDestinationSession() {
  const { selectSession, createSession, listSessions, sessions } =
    useChatSessions();
  const { getLocation } = useLocation();
  const { showToast } = useToast();
  const { t } = useLanguage();

  const [isWorking, setIsWorking] = useState(false);
  const busyRef = useRef(false);

  // Shared tail: bind a resolved agent to a session (reuse-or-create) and
  // select it. Mirrors RN's bindAgentSession (incl. the brief settle wait) so
  // every entry point — destination search and direct guide pick — inits
  // identically.
  const bindAgentSession = useCallback(
    async (
      agent: { id: string },
      title: string,
      destLocation:
        | { lat: number; lng: number; name: string; timezone?: string }
        | undefined,
    ): Promise<EnsureResult> => {
      try {
        await listSessions();
      } catch {
        /* non-fatal — fall back to whatever is cached */
      }
      await new Promise(resolve => setTimeout(resolve, 100));

      const existing = sessions.find(s => s.agentId === agent.id);
      if (existing) {
        await selectSession(existing.id);
        return { sessionId: existing.id, agentId: agent.id };
      }

      const session = await createSession(title, agent.id, true, destLocation);
      return { sessionId: session?.id ?? null, agentId: agent.id };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions],
  );

  /**
   * Direct bind to a specific guide chosen from the DB-driven guide catalog
   * (GET /chat/guides). Works for both location guides and experience overlays
   * (which have no geo location to resolve), so the agent switcher can offer
   * the full roster without round-tripping through geocoding.
   */
  const ensureByAgent = useCallback(
    async (agentId: string, title: string): Promise<EnsureResult> => {
      if (busyRef.current) return { sessionId: null };
      busyRef.current = true;
      setIsWorking(true);
      try {
        return await bindAgentSession({ id: agentId }, title, undefined);
      } catch (e) {
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? (e as Error).message
            : t('FAILED_TO_SELECT_DESTINATION') || 'Could not switch guide';
        showToast(msg, { variant: 'error' });
        return { sessionId: null };
      } finally {
        setIsWorking(false);
        busyRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bindAgentSession],
  );

  const ensureSession = useCallback(
    async (search: string): Promise<EnsureResult> => {
      if (busyRef.current) return { sessionId: null };
      busyRef.current = true;
      setIsWorking(true);
      try {
        const locRes: any = await getLocation({ search });
        const matched = locRes?.location?.matched_location;
        const destLocation = matched
          ? {
              lat: matched.lat,
              lng: matched.lon,
              name: search,
              timezone: matched.timezone,
            }
          : undefined;

        if (!locRes?.agents?.length) {
          showToast(
            t('NO_AGENT_FOUND_FOR_DESTINATION') ||
              'No guide found for that destination',
            { variant: 'error' },
          );
          return { sessionId: null };
        }

        return await bindAgentSession(locRes.agents[0], search, destLocation);
      } catch (e) {
        const msg =
          e && typeof e === 'object' && 'message' in e
            ? (e as Error).message
            : t('FAILED_TO_SELECT_DESTINATION') || 'Could not switch guide';
        showToast(msg, { variant: 'error' });
        return { sessionId: null };
      } finally {
        setIsWorking(false);
        busyRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bindAgentSession],
  );

  return {
    ensureSession,
    ensureByAgent,
    isWorking,
    sessions,
    selectSession,
    listSessions,
  };
}

export default useEnsureDestinationSession;
